import type { SubjectLeftAction } from '@Application/Subjects/Actions/subjectLeft';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import computeInterviewPayload from '@Application/Beneficiaries/Interviews/Actions/computeInterviewPayload';
import type { InterviewChangedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewChanged';
import type { InterviewRenameFailedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewRenameFailed';
import type { InterviewRenamedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewRenamed';
import type { InterviewRemovedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewRemoved';
import type { InterviewConsentFetchedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewConsentFetched';
import type { InterviewConsentWithdrawnAction } from '@Application/Beneficiaries/Interviews/Actions/interviewConsentWithdrawn';
import type { InterviewCreatedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewCreated';
import type { InterviewCreationFailedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewCreationFailed';
import type { InterviewDeletedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewDeleted';
import type { InterviewDeletionFailedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewDeletionFailed';
import type { InterviewFetchedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewFetched';
import type { InterviewsFetchedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewsFetched';
import type { RecordingCompletedAction } from '@Application/Beneficiaries/Interviews/Actions/recordingCompleted';
import type { RecordingCompletionFailedAction } from '@Application/Beneficiaries/Interviews/Actions/recordingCompletionFailed';
import type { RecordingSegmentTransferFailedAction } from '@Application/Beneficiaries/Interviews/Actions/recordingSegmentTransferFailed';
import type { RecordingSegmentTransferredAction } from '@Application/Beneficiaries/Interviews/Actions/recordingSegmentTransferred';
import type { RecordingSegmentsFetchFailedAction } from '@Application/Beneficiaries/Interviews/Actions/recordingSegmentsFetchFailed';
import type { RecordingSegmentsFetchedAction } from '@Application/Beneficiaries/Interviews/Actions/recordingSegmentsFetched';
import type { RecordingTransferStartedAction } from '@Application/Beneficiaries/Interviews/Actions/recordingTransferStarted';
import type { TranscriptFetchedAction } from '@Application/Beneficiaries/Transcripts/Actions/transcriptFetched';
import type { TranscriptReviewedAction } from '@Application/Beneficiaries/Transcripts/Actions/transcriptReviewed';
import type { TranscriptSpeakerAssignedAction } from '@Application/Beneficiaries/Transcripts/Actions/transcriptSpeakerAssigned';
import type { TranscriptSpeakerUnassignedAction } from '@Application/Beneficiaries/Transcripts/Actions/transcriptSpeakerUnassigned';
import type { TranscriptSpeakersFetchedAction } from '@Application/Beneficiaries/Transcripts/Actions/transcriptSpeakersFetched';
import type { TranscriptSpeakersMergedAction } from '@Application/Beneficiaries/Transcripts/Actions/transcriptSpeakersMerged';
import type { TranscriptTurnRevisedAction } from '@Application/Beneficiaries/Transcripts/Actions/transcriptTurnRevised';
import type { TranscriptTurnsFetchedAction } from '@Application/Beneficiaries/Transcripts/Actions/transcriptTurnsFetched';
import InterviewConsentView from '@UI/Store/Interviews/Views/InterviewConsentView';
import InterviewView from '@UI/Store/Interviews/Views/InterviewView';
import InterviewsListView from '@UI/Store/Interviews/Views/InterviewsListView';
import RecordingView from '@UI/Store/Interviews/Views/RecordingView';
import TranscriptSpeakerView from '@UI/Store/Interviews/Views/TranscriptSpeakerView';
import TranscriptSpeakersListView from '@UI/Store/Interviews/Views/TranscriptSpeakersListView';
import TranscriptTurnView from '@UI/Store/Interviews/Views/TranscriptTurnView';
import TranscriptTurnsListView from '@UI/Store/Interviews/Views/TranscriptTurnsListView';
import TranscriptView from '@UI/Store/Interviews/Views/TranscriptView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type InterviewsStore = {
	interviews: InterviewsListView | null;
	interview: InterviewView | null;
	consent: InterviewConsentView | null;
	transcript: TranscriptView | null;
	transcriptTurns: TranscriptTurnsListView | null;
	transcriptSpeakers: TranscriptSpeakersListView | null;
};

const initialState: InterviewsStore = {
	interviews: null,
	interview: null,
	consent: null,
	transcript: null,
	transcriptTurns: null,
	transcriptSpeakers: null,
};

type Actions =
	| InterviewsFetchedAction
	| InterviewFetchedAction
	| InterviewChangedAction
	| InterviewRenamedAction
	| InterviewRenameFailedAction
	| InterviewRemovedAction
	| InterviewCreatedAction
	| InterviewDeletedAction
	// The failure reaches the notification only: the server's message is the whole story.
	| InterviewDeletionFailedAction
	| RecordingSegmentsFetchedAction
	| RecordingSegmentTransferredAction
	| RecordingCompletedAction
	// Reaches the event bus, no state to change: an invented "transfer in progress" flag on the
	// recording would duplicate `OutboxItem.state`, which already carries this fact and is already
	// observed live — the very defect this store fix corrects, reproduced in the other direction.
	| RecordingTransferStartedAction
	// The four failures are already readable where they belong, `OutboxItem.lastErrorMessage`, in
	// French: nothing here needs a second copy of an error message.
	| InterviewCreationFailedAction
	| RecordingSegmentsFetchFailedAction
	| RecordingSegmentTransferFailedAction
	| RecordingCompletionFailedAction
	| InterviewConsentFetchedAction
	| InterviewConsentWithdrawnAction
	| TranscriptFetchedAction
	| TranscriptTurnsFetchedAction
	| TranscriptTurnRevisedAction
	| TranscriptSpeakersFetchedAction
	| TranscriptSpeakerAssignedAction
	| TranscriptSpeakerUnassignedAction
	| TranscriptSpeakersMergedAction
	| TranscriptReviewedAction
	| SubjectLeftAction
	| UserRevokedAction;

const whenInterviewsFetched = (store: InterviewsStore, action: InterviewsFetchedAction): InterviewsStore => {
	const list = new InterviewsListView(
		action.payload.results.map((interview) => InterviewView.fromPayload(interview)),
		action.typeKey,
		action.subjectId,
		action.payload.totalRecords,
	);

	return { ...store, interviews: list };
};

const whenInterviewFetched = (store: InterviewsStore, action: InterviewFetchedAction): InterviewsStore => ({
	...store,
	interview: InterviewView.fromPayload(action.payload),
});

const isListOfStream = (
	list: InterviewsListView | null,
	action: InterviewChangedAction | InterviewRenamedAction | InterviewRemovedAction,
): list is InterviewsListView => list?.isFor(new SubjectAddress(action.typeKey, action.subjectId)) === true;

const replaceHeldRow = (
	list: InterviewsListView | null,
	interview: InterviewView,
	action: InterviewChangedAction | InterviewRenamedAction,
): InterviewsListView | null =>
	isListOfStream(list, action) && list.findById(interview.id) !== null
		? list.updateInterview(interview)
		: list;

// The dossier event stream and the rename answer carry the interview as the `GET` answers it, `conductedOn`
// still a wire string. The row of the same id is replaced in the list of that dossier (never added), and so
// is the open detail.
const whenInterviewChanged = (
	store: InterviewsStore,
	action: InterviewChangedAction | InterviewRenamedAction,
): InterviewsStore => {
	const changed = InterviewView.fromPayload(computeInterviewPayload(action.payload));

	return {
		...store,
		interviews: replaceHeldRow(store.interviews, changed, action),
		interview: store.interview?.id === changed.id ? changed : store.interview,
	};
};

const whenInterviewRemoved = (store: InterviewsStore, action: InterviewRemovedAction): InterviewsStore => {
	const { id } = action.payload;
	const { interviews } = store;

	return {
		...store,
		interviews: isListOfStream(interviews, action) ? interviews.removeInterview(id) : interviews,
		interview: store.interview?.id === id ? null : store.interview,
	};
};

/**
 * The list this interview was born in learns about it straight from the reducer, at the moment the
 * transfer handler creates it — never by hoping a screen effect reloads the list later. That is what
 * keeps the interview visible even when no dossier screen is mounted at the time of creation:
 * `payload` is already a `ComputedInterviewPayload` (computed in the action creator, same motif as
 * `interviewFetched`/`interviewsFetched`), so it is handed to `InterviewView.fromPayload` unchanged.
 */
const whenInterviewCreated = (store: InterviewsStore, action: InterviewCreatedAction): InterviewsStore => {
	if (
		store.interviews === null ||
		!store.interviews.isFor(new SubjectAddress(action.typeKey, action.subjectId))
	) {
		return store;
	}

	return {
		...store,
		interviews: store.interviews.upsertInterview(InterviewView.fromPayload(action.payload)),
	};
};

// The deleted interview leaves the list of its dossier, and the detail is cleared only when it is that
// very interview.
const whenInterviewDeleted = (store: InterviewsStore, action: InterviewDeletedAction): InterviewsStore => {
	const { interviewId } = action.payload;

	return {
		...store,
		interviews: store.interviews?.isFor(new SubjectAddress(action.typeKey, action.subjectId))
			? store.interviews.removeInterview(interviewId)
			: store.interviews,
		interview: store.interview?.id === interviewId ? null : store.interview,
	};
};

/**
 * Shared by the two segment actions: locates the row under ownership, then only ever builds new
 * instances (`RecordingView`/`InterviewView`/`InterviewsListView`) when the count actually changes.
 * Returning `store` unchanged when it does not is not an optimisation — it is what makes a replayed
 * action (or a segment already counted) produce the exact same state reference, so a dispatch that
 * says nothing new never triggers a re-render.
 */
const applyRecordingSegmentCount = (
	store: InterviewsStore,
	subject: SubjectAddress,
	interviewId: number,
	nextSegmentCount: number,
): InterviewsStore => {
	if (store.interviews === null || !store.interviews.isFor(subject)) {
		return store;
	}

	const existingInterview = store.interviews.findById(interviewId);

	if (existingInterview === null || existingInterview.recording === null) {
		return store;
	}

	if (existingInterview.recording.segmentCount === nextSegmentCount) {
		return store;
	}

	const nextRecording = existingInterview.recording.withSegmentCount(nextSegmentCount);

	return {
		...store,
		interviews: store.interviews.updateInterview(existingInterview.withRecording(nextRecording)),
	};
};

// `segmentCount` is set to the server's own count — the baseline a resumed transfer skips ahead
// from — never derived from `OutboxItem.progress` (a local, byte-level measure of a different fact).
const whenRecordingSegmentsFetched = (
	store: InterviewsStore,
	action: RecordingSegmentsFetchedAction,
): InterviewsStore =>
	applyRecordingSegmentCount(
		store,
		new SubjectAddress(action.typeKey, action.subjectId),
		action.interviewId,
		action.payload.length,
	);

// `position` keeps the wire field's own name (`RecordingSegmentPayload`, never renamed to `segment`
// here). `segmentCount` only ever climbs: a resumed transfer may re-confirm a segment the store
// already counted, and that replay must never make the count step backwards.
const whenRecordingSegmentTransferred = (
	store: InterviewsStore,
	action: RecordingSegmentTransferredAction,
): InterviewsStore => {
	if (
		store.interviews === null ||
		!store.interviews.isFor(new SubjectAddress(action.typeKey, action.subjectId))
	) {
		return store;
	}

	const existingInterview = store.interviews.findById(action.interviewId);

	if (existingInterview === null || existingInterview.recording === null) {
		return store;
	}

	const nextSegmentCount = Math.max(existingInterview.recording.segmentCount, action.payload.position + 1);

	return applyRecordingSegmentCount(
		store,
		new SubjectAddress(action.typeKey, action.subjectId),
		action.interviewId,
		nextSegmentCount,
	);
};

const whenRecordingCompleted = (
	store: InterviewsStore,
	action: RecordingCompletedAction,
): InterviewsStore => {
	// Confirming a recording creates a processing server-side that the response does not carry, so the
	// list of this dossier is invalidated to be read again. Reached only after a successful network
	// round-trip, so it never blanks a screen while offline.
	const isListInvalidated =
		store.interviews?.isFor(new SubjectAddress(action.typeKey, action.subjectId)) === true;

	return {
		...store,
		interviews: isListInvalidated ? null : store.interviews,
		interview:
			store.interview?.id === action.interviewId
				? store.interview.withRecording(RecordingView.fromPayload(action.payload, action.interviewId))
				: store.interview,
	};
};

const patchInterview = (
	interview: InterviewView | null,
	interviewId: number,
	patch: (current: InterviewView) => InterviewView,
): InterviewView | null => {
	if (interview === null || interview.id !== interviewId) {
		return interview;
	}

	return patch(interview);
};

const patchInterviewsList = (
	interviews: InterviewsListView | null,
	subject: SubjectAddress,
	interviewId: number,
	patch: (current: InterviewView) => InterviewView,
): InterviewsListView | null => {
	if (interviews === null || !interviews.isFor(subject)) {
		return interviews;
	}

	const existing = interviews.findById(interviewId);

	if (existing === null) {
		return interviews;
	}

	return interviews.updateInterview(patch(existing));
};

const whenInterviewConsentFetched = (
	store: InterviewsStore,
	action: InterviewConsentFetchedAction,
): InterviewsStore => ({
	...store,
	consent: new InterviewConsentView(action.payload, action.interviewId),
});

// Three effects, none optional: the server has just deleted the recording row and marked the consent
// withdrawn.
// 1. Replace `consent` only under ownership (interviewId match).
// 2. On both the open detail and the matching list entry: replace `consent` too — an object changes
//    at withdrawal (`withdrawnAt` moves from `null` to a date), unlike the bare id this View used to
//    carry, which never changed on withdrawal and so needed no update here.
// 3. Clear `recording` on both the open detail and the matching list entry.
const whenInterviewConsentWithdrawn = (
	store: InterviewsStore,
	action: InterviewConsentWithdrawnAction,
): InterviewsStore => {
	const withdrawnConsent = new InterviewConsentView(action.payload, action.interviewId);

	return {
		...store,
		consent: store.consent?.interviewId === action.interviewId ? withdrawnConsent : store.consent,
		interview: patchInterview(store.interview, action.interviewId, (current) =>
			current.withoutRecording(withdrawnConsent),
		),
		interviews: patchInterviewsList(
			store.interviews,
			new SubjectAddress(action.typeKey, action.subjectId),
			action.interviewId,
			(current) => current.withoutRecording(withdrawnConsent),
		),
	};
};

// The server answers the same shape on the fetch and on the launch: both replace `transcript`
// unconditionally, the entering payload always being authoritative — same motif as `whenInterviewFetched`.
const whenTranscriptFetched = (store: InterviewsStore, action: TranscriptFetchedAction): InterviewsStore => ({
	...store,
	transcript: new TranscriptView(action.payload),
});

// Fetch handlers replace the view unconditionally (except page accumulation below): the entering
// payload is authoritative. Transposing the mutation handlers' ownership guard here would freeze the
// screen on the second interview visited in a session, because `stopOn` would stay false forever.
const whenTranscriptTurnsFetched = (
	store: InterviewsStore,
	action: TranscriptTurnsFetchedAction,
): InterviewsStore => {
	const turns = action.payload.results.map((turnPayload) => new TranscriptTurnView(turnPayload));

	if (action.page === 0) {
		return {
			...store,
			transcriptTurns: new TranscriptTurnsListView(
				turns,
				action.interviewId,
				action.payload.totalRecords,
				1,
			),
		};
	}

	if (store.transcriptTurns === null) {
		return store;
	}

	return {
		...store,
		transcriptTurns: store.transcriptTurns.appendPage(action.page, turns),
	};
};

// The server invalidates the review and may bump `revisedTurnCount` in the same response (§5.3): both
// branches move together, or the screen keeps showing a review that no longer holds.
const whenTranscriptTurnRevised = (
	store: InterviewsStore,
	action: TranscriptTurnRevisedAction,
): InterviewsStore => {
	const revisedTurn = new TranscriptTurnView(action.payload.turn);

	return {
		...store,
		transcriptTurns:
			store.transcriptTurns !== null ? store.transcriptTurns.replaceTurn(revisedTurn) : null,
		transcript: new TranscriptView(action.payload.transcript),
	};
};

const whenTranscriptSpeakersFetched = (
	store: InterviewsStore,
	action: TranscriptSpeakersFetchedAction,
): InterviewsStore => ({
	...store,
	transcriptSpeakers: new TranscriptSpeakersListView(
		action.payload.map((speakerPayload) => new TranscriptSpeakerView(speakerPayload)),
		action.interviewId,
	),
});

const whenTranscriptSpeakerAssigned = (
	store: InterviewsStore,
	action: TranscriptSpeakerAssignedAction,
): InterviewsStore => {
	if (store.transcriptSpeakers === null) {
		return store;
	}

	return {
		...store,
		transcriptSpeakers: store.transcriptSpeakers.replaceSpeaker(
			new TranscriptSpeakerView(action.payload),
		),
	};
};

const whenTranscriptSpeakerUnassigned = (
	store: InterviewsStore,
	action: TranscriptSpeakerUnassignedAction,
): InterviewsStore => {
	if (store.transcriptSpeakers === null) {
		return store;
	}

	return {
		...store,
		transcriptSpeakers: store.transcriptSpeakers.replaceSpeaker(
			new TranscriptSpeakerView(action.payload),
		),
	};
};

// A merge reassigns N turns server-side; the in-memory turn views still carry the old `speakerId`.
// Invalidating `transcriptTurns` (rather than patching each turn) avoids duplicating that server rule
// in the store — it costs a refetch of the first page, which is acceptable for a rare, irreversible action.
const whenTranscriptSpeakersMerged = (
	store: InterviewsStore,
	action: TranscriptSpeakersMergedAction,
): InterviewsStore => {
	if (store.transcriptSpeakers === null) {
		return {
			...store,
			transcriptTurns: null,
		};
	}

	return {
		...store,
		transcriptSpeakers: store.transcriptSpeakers
			.replaceSpeaker(new TranscriptSpeakerView(action.payload))
			.removeSpeaker(action.sourceSpeakerId),
		transcriptTurns: null,
	};
};

const whenTranscriptReviewed = (
	store: InterviewsStore,
	action: TranscriptReviewedAction,
): InterviewsStore => ({
	...store,
	transcript: new TranscriptView(action.payload),
});

const whenSubjectLeft = (store: InterviewsStore, action: SubjectLeftAction): InterviewsStore => ({
	...store,
	interviews: store.interviews?.isFor(new SubjectAddress(action.payload.typeKey, action.payload.subjectId))
		? null
		: store.interviews,
	interview: null,
	consent: null,
	transcript: null,
	transcriptTurns: null,
	transcriptSpeakers: null,
});

const whenUserRevoked = (): InterviewsStore => initialState;

// `createSliceReducer`'s handler map is exhaustive over `Actions`: the departure action and the four
// failures still need an entry each, even though none of them changes this store's state (see the
// union above for why).
const identity = (store: InterviewsStore): InterviewsStore => store;

export default registerStore<'interviewsStore', Actions>(
	'interviewsStore',
	createSliceReducer({
		INTERVIEWS_FETCHED: whenInterviewsFetched,
		INTERVIEW_FETCHED: whenInterviewFetched,
		INTERVIEW_CHANGED: whenInterviewChanged,
		INTERVIEW_RENAMED: whenInterviewChanged,
		INTERVIEW_RENAME_FAILED: identity,
		INTERVIEW_REMOVED: whenInterviewRemoved,
		INTERVIEW_CREATED: whenInterviewCreated,
		INTERVIEW_DELETED: whenInterviewDeleted,
		RECORDING_SEGMENTS_FETCHED: whenRecordingSegmentsFetched,
		RECORDING_SEGMENT_TRANSFERRED: whenRecordingSegmentTransferred,
		RECORDING_COMPLETED: whenRecordingCompleted,
		INTERVIEW_DELETION_FAILED: identity,
		RECORDING_TRANSFER_STARTED: identity,
		INTERVIEW_CREATION_FAILED: identity,
		RECORDING_SEGMENTS_FETCH_FAILED: identity,
		RECORDING_SEGMENT_TRANSFER_FAILED: identity,
		RECORDING_COMPLETION_FAILED: identity,
		INTERVIEW_CONSENT_FETCHED: whenInterviewConsentFetched,
		INTERVIEW_CONSENT_WITHDRAWN: whenInterviewConsentWithdrawn,
		TRANSCRIPT_FETCHED: whenTranscriptFetched,
		TRANSCRIPT_TURNS_FETCHED: whenTranscriptTurnsFetched,
		TRANSCRIPT_TURN_REVISED: whenTranscriptTurnRevised,
		TRANSCRIPT_SPEAKERS_FETCHED: whenTranscriptSpeakersFetched,
		TRANSCRIPT_SPEAKER_ASSIGNED: whenTranscriptSpeakerAssigned,
		TRANSCRIPT_SPEAKER_UNASSIGNED: whenTranscriptSpeakerUnassigned,
		TRANSCRIPT_SPEAKERS_MERGED: whenTranscriptSpeakersMerged,
		TRANSCRIPT_REVIEWED: whenTranscriptReviewed,
		SUBJECT_LEFT: whenSubjectLeft,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
